import { defineStore } from 'pinia'
import { errorMessage } from '@/services/http'
import { filesService } from '@/services/files'
import { subscribe } from '@/services/events'
import { useJobsStore } from './jobs'
import { useBridgeStore } from './bridge'
import { useNotificationsStore } from './notifications'
import { useMaterialsStore } from './materials'
import { useWatermarksStore } from './watermarks'
import { useAuthStore } from './auth'
import { usePaymentStore } from './payment'
import { FILE_CATEGORY, FILE_SOURCE } from '@/types/api'
import { uploadRejection } from '@/constants/formats'
import { resolveSettings } from '@/constants/settings'
import { resolveOverrides } from '@/constants/materials'
import { applyBatch, summarize } from '@/utils/batch'
import { triggerDownload } from '@/utils/download'
import type { Skipped } from '@/types/api'
import type {
  FileCategory,
  FileSettings,
  MaterialOverrides,
  UserFile,
} from '@/types/api'

let unsubscribe: (() => void) | null = null

const saveTimers = new Map<string, ReturnType<typeof setTimeout>>()

const materialTimers = new Map<string, ReturnType<typeof setTimeout>>()

const inflight = new Map<string, Promise<unknown>>()

function chain<T>(id: string, run: () => Promise<T>): Promise<T> {
  const result = (inflight.get(id) ?? Promise.resolve()).then(run, run)
  const settled = result.then(
    () => undefined,
    () => undefined,
  )
  inflight.set(id, settled)
  void settled.then(() => {
    if (inflight.get(id) === settled) inflight.delete(id)
  })
  return result
}

const removing = new Set<string>()

const UPLOAD_CONCURRENCY = 4
const SAVE_DEBOUNCE_MS = 400

type DraftBody = FileSettings | MaterialOverrides

interface DraftConfig {
  draft: 'settingsDraft' | 'materialsDraft'
  revision: 'settingsRevision' | 'materialsRevision'
  timers: Map<string, ReturnType<typeof setTimeout>>
  save: (id: string, body: DraftBody) => Promise<UserFile>
}

const DRAFTS = {
  settings: {
    draft: 'settingsDraft',
    revision: 'settingsRevision',
    timers: saveTimers,
    save: (id, body) => filesService.saveSettings(id, body as FileSettings),
  },
  materials: {
    draft: 'materialsDraft',
    revision: 'materialsRevision',
    timers: materialTimers,
    save: (id, body) => filesService.saveMaterials(id, body as MaterialOverrides),
  },
} satisfies Record<string, DraftConfig>

type DraftKind = keyof typeof DRAFTS

const DRAFT_KINDS = Object.keys(DRAFTS) as DraftKind[]

interface FilesState {
  items: UserFile[]
  loaded: Partial<Record<FileCategory, boolean>>
  pending: Partial<Record<FileCategory, boolean>>
  previewId: string | null
  checked: Record<string, true>
  search: string
  loading: boolean
  uploading: number
  error: string | null
  settingsDraft: Record<string, FileSettings>
  settingsRevision: Record<string, number>
  materialsDraft: Record<string, MaterialOverrides>
  materialsRevision: Record<string, number>
  materialNames: Record<string, string[]>
  hoveredSlot: number | null
  previewLoading: boolean
}

export const useFilesStore = defineStore('files', {
  state: (): FilesState => ({
    items: [],
    loaded: {},
    pending: {},
    previewId: null,
    checked: {},
    search: '',
    loading: false,
    uploading: 0,
    error: null,
    settingsDraft: {},
    settingsRevision: {},
    materialsDraft: {},
    materialsRevision: {},
    materialNames: {},
    hoveredSlot: null,
    previewLoading: false,
  }),

  getters: {
    previewed: (state): UserFile | null =>
      state.items.find((f) => f.id === state.previewId) ?? null,

    checkedFiles: (state): UserFile[] => state.items.filter((f) => state.checked[f.id]),

    checkedCount(): number {
      return this.checkedFiles.length
    },

    isChecked: (state) => (id: string): boolean => state.checked[id] === true,

    loadedIn: (state) => (category: FileCategory): boolean => state.loaded[category] === true,

    visibleIn: (state) => {
      const term = state.search.trim().toLowerCase()
      return (category: FileCategory): UserFile[] =>
        state.items.filter(
          (f) =>
            f.category === category && (!term || f.filename.toLowerCase().includes(term)),
        )
    },

    settingsFor: (state) => (id: string | null | undefined): FileSettings => {
      if (!id) return resolveSettings(undefined)
      const draft = state.settingsDraft[id]
      if (draft) return draft
      return resolveSettings(state.items.find((f) => f.id === id)?.file_settings)
    },

    overridesFor: (state) => (id: string | null | undefined): MaterialOverrides => {
      if (!id) return {}
      const draft = state.materialsDraft[id]
      if (draft) return draft
      return resolveOverrides(state.items.find((f) => f.id === id)?.material_overrides)
    },

    materialsFor: (state) => (id: string | null | undefined): string[] =>
      id ? (state.materialNames[id] ?? []) : [],
  },

  actions: {
    async fetchCategory(category: FileCategory): Promise<void> {
      if (this.pending[category]) return
      this.pending[category] = true
      this.loading = true
      this.error = null
      try {
        const fetched = await filesService.list(category)
        const live = new Set(fetched.map((f) => f.id))

        for (const file of this.items) {
          if (file.category === category && !live.has(file.id)) delete this.checked[file.id]
        }

        this.items = this.items
          .filter((f) => f.category !== category)
          .concat(fetched)
          .sort((a, b) => b.created_date.localeCompare(a.created_date))

        this.loaded[category] = true

        if (!this.previewId) {
          const opening =
            this.items.find((f) => f.category === FILE_CATEGORY.model) ?? this.items[0]
          this.previewId = opening?.id ?? null
        }
      } catch (e) {
        this.error = errorMessage(e)
      } finally {
        delete this.pending[category]
        this.loading = false
      }
    },

    async ensureCategory(category: FileCategory): Promise<void> {
      if (this.loaded[category]) return
      await this.fetchCategory(category)
    },

    async refetchLoaded(): Promise<void> {
      const categories = Object.values(FILE_CATEGORY).filter((c) => this.loaded[c])
      const targets = categories.length ? categories : [FILE_CATEGORY.model]
      await Promise.all(targets.map((c) => this.fetchCategory(c)))
    },

    preview(id: string | null): void {
      this.previewId = id
    },

    toggleChecked(id: string): void {
      if (this.checked[id]) delete this.checked[id]
      else this.checked[id] = true
    },

    clearChecked(): void {
      this.checked = {}
    },

    setMaterialNames(id: string, names: string[]): void {
      this.materialNames[id] = names
    },

    setHoveredSlot(index: number | null): void {
      this.hoveredSlot = index
    },

    setPreviewLoading(value: boolean): void {
      this.previewLoading = value
    },

    appendError(message: string): void {
      this.error = [this.error, message].filter(Boolean).join(' ')
    },

    applyFile(file: UserFile): void {
      if (file.category === FILE_CATEGORY.watermark) return
      if (removing.has(file.id)) return
      const index = this.items.findIndex((f) => f.id === file.id)
      if (index === -1) this.items.unshift(file)
      else this.items[index] = file
      if (!this.previewId) this.previewId = file.id
    },

    async uploadPicked(picked: File[]): Promise<void> {
      this.error = null

      const uploadable: File[] = []
      const reasons = new Set<string>()

      for (const file of picked) {
        const reason = uploadRejection(file)
        if (reason) reasons.add(reason)
        else uploadable.push(file)
      }

      const skipped = picked.length - uploadable.length
      if (skipped) {
        const prefix = uploadable.length ? `Skipped ${skipped} of ${picked.length} files.` : ''
        this.appendError([prefix, ...reasons].filter(Boolean).join(' '))
      }

      if (!uploadable.length) return

      await this.uploadMany(uploadable)
    },

    async uploadMany(files: File[]): Promise<void> {
      this.uploading += files.length

      let cursor = 0
      const worker = async (): Promise<void> => {
        for (;;) {
          const next = files[cursor++]
          if (!next) return
          try {
            this.applyFile(await filesService.upload(next))
          } catch (e) {
            this.appendError(errorMessage(e))
          } finally {
            this.uploading -= 1
          }
        }
      }

      await Promise.all(
        Array.from({ length: Math.min(UPLOAD_CONCURRENCY, files.length) }, worker),
      )
    },

    async convertMany(files: UserFile[], format: string): Promise<void> {
      if (!files.length) return

      const jobs = useJobsStore()
      this.error = null

      await jobs.runBatch(async () => {
        try {
          const batch = await filesService.convert(
            files.map((f) => f.id),
            format,
          )
          applyBatch(
            'convert',
            batch,
            (job) => jobs.applyJob(job),
            (message) => this.appendError(message),
          )
        } catch (e) {
          this.appendError(errorMessage(e))
        }
      })
    },

    patchDraft(kind: DraftKind, id: string, next: DraftBody): void {
      const config = DRAFTS[kind]
      ;(this[config.draft] as Record<string, DraftBody>)[id] = next
      this[config.revision][id] = (this[config.revision][id] ?? 0) + 1

      clearTimeout(config.timers.get(id))
      config.timers.set(
        id,
        setTimeout(() => void this.flushDraft(kind, id), SAVE_DEBOUNCE_MS),
      )
    },

    async flushDraft(kind: DraftKind, id: string): Promise<void> {
      const config = DRAFTS[kind]
      clearTimeout(config.timers.get(id))
      config.timers.delete(id)

      const revision = this[config.revision][id]
      const body = this[config.draft][id]
      if (revision === undefined || !body) return

      try {
        this.applyFile(await chain(id, () => config.save(id, body)))
        if (this[config.revision][id] === revision) {
          delete this[config.draft][id]
          delete this[config.revision][id]
        }
      } catch (e) {
        this.error = errorMessage(e)
      }
    },

    updateSettings(id: string, next: FileSettings): void {
      this.patchDraft('settings', id, next)
    },

    updateMaterials(id: string, next: MaterialOverrides): void {
      this.patchDraft('materials', id, next)
    },

    async flushPending(id: string): Promise<void> {
      await Promise.all(DRAFT_KINDS.map((kind) => this.flushDraft(kind, id)))
    },

    async flushAll(): Promise<void> {
      const ids = new Set(DRAFT_KINDS.flatMap((kind) => [...DRAFTS[kind].timers.keys()]))
      await Promise.all([...ids].map((id) => this.flushPending(id)))
    },

    discardDrafts(id: string): void {
      for (const kind of DRAFT_KINDS) {
        const config = DRAFTS[kind]
        clearTimeout(config.timers.get(id))
        config.timers.delete(id)
        delete this[config.draft][id]
        delete this[config.revision][id]
      }
      delete this.materialNames[id]
    },

    async removeMany(files: UserFile[]): Promise<void> {
      if (!files.length) return

      this.error = null
      const doomed = new Set(files.map((f) => f.id))

      for (const file of files) {
        this.discardDrafts(file.id)
        delete this.checked[file.id]
        removing.add(file.id)
      }

      this.items = this.items.filter((f) => !doomed.has(f.id))
      if (this.previewId && doomed.has(this.previewId)) {
        this.previewId = this.items[0]?.id ?? null
      }

      try {
        await filesService.remove([...doomed])
      } catch (e) {
        this.appendError(errorMessage(e))
        this.items.push(...files)
        this.items.sort((a, b) => b.created_date.localeCompare(a.created_date))
        for (const file of files) this.checked[file.id] = true
      }

      for (const file of files) removing.delete(file.id)
    },

    downloadMany(files: UserFile[]): void {
      this.error = null

      const skipped: Skipped[] = files
        .filter((f) => f.source !== FILE_SOURCE.uploaded)
        .map((f) => ({ filename: f.filename, reason: 'linked models stay on your machine' }))

      const stored = files.filter((f) => f.source === FILE_SOURCE.uploaded)
      if (stored.length) triggerDownload(filesService.downloadUrl(stored.map((f) => f.id)))

      const summary = summarize('download', stored.length, skipped)
      if (summary) this.appendError(summary)
    },

    watch(): void {
      if (unsubscribe) return
      const jobs = useJobsStore()
      const bridge = useBridgeStore()
      const notifications = useNotificationsStore()
      const watermarks = useWatermarksStore()
      const materials = useMaterialsStore()
      const auth = useAuthStore()
      const payment = usePaymentStore()
      const refetch = (): void => {
        void this.refetchLoaded()
        if (watermarks.loaded) void watermarks.refresh()
        if (materials.loaded) void materials.refresh()
        void jobs.fetchQueue()
        void bridge.fetchStatus()
        void notifications.fetch()
      }
      unsubscribe = subscribe({
        file: (file) => this.applyFile(file),
        job: (job) => jobs.applyJob(job),
        bridge: (status) => bridge.apply(status),
        notification: () => void notifications.fetch(),
        material: (material) => materials.apply(material),
        credits: (credits) => auth.applyCredits(credits),
        payment: (settled) => {
          payment.apply(settled)
          auth.applyCredits(settled.balance)
        },
        resync: refetch,
        reconnected: refetch,
      })
    },

    unwatch(): void {
      unsubscribe?.()
      unsubscribe = null
      void this.flushAll()
    },

    resetSession(): void {
      for (const kind of DRAFT_KINDS) {
        const { timers } = DRAFTS[kind]
        for (const timer of timers.values()) clearTimeout(timer)
        timers.clear()
      }
      inflight.clear()
      this.$reset()
    },
  },
})
